// TAX2-S1-F1 (docs/build-briefs/TAX2-S1-F1-tax-rate-settings.md; DEC-304, DEC-307; design/mockups/
// tax2-s1-f1-tax-rate-settings.html). The Sales tax section of Settings, Money and pricing: the
// rate in effect, the scheduled change, the history, and the form that records a new rate.
//
// Admin surface, touch only: every control is 44px or taller, nothing depends on hover, the Save
// button sits at the end of the form (never fixed) so the on-screen keyboard cannot cover it, and
// every sentence a person reads comes from ui/src/taxRateSettingsModel.ts. Text is set with
// textContent only. A session without admin.tax_rate.manage is shown the combined rate alone and
// never calls the tax-rates routes (DEC-307).
//
// The form is built once per open and updated in place, so typing never rebuilds an input and
// never loses focus or the on-screen keyboard.

import { hoverHelp } from "./hoverHelp.ts";
import {
  getLocationMoneyConfirmation,
  getTaxRates,
  postLocationMoneyConfirmation,
  putTaxRates,
  type TaxRatesDto,
} from "./api.ts";
import {
  buildPutBody,
  buildRateView,
  checkForm,
  daysText,
  formatDay,
  formatPercent,
  initialFormState,
  mapRefusal,
  MAX_START_DATE,
  MIN_START_DATE,
  pastDateWarning,
  recordedMessage,
  requestSignature,
  startPresets,
  totalLabel,
  stepPercentText,
  TAX_PART_HELP,
  TAX_PART_LABELS,
  TAX_PART_ORDER,
  todayInZone,
  type FormMode,
  type PartRow,
  type PlainRefusal,
  type RateContext,
  type RateFormState,
  type RateView,
  type SetView,
  type TaxPartKey,
} from "./taxRateSettingsModel.ts";

export interface TaxRateSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.tax_rate.manage. */
  canManage: () => boolean;
  /** The bound a rate must stay inside, in words ("0.00% to 100.00%"), from the validator's constant. */
  rangeText: () => string;
  /** The combined rate from the ordinary settings read, in the wire unit. */
  combinedUnits: () => number;
  /** The location's business day zone, and whether it is still the unconfirmed default (CL6-F1-F3). */
  zone: () => { timeZone: string; unconfirmedUtc: boolean };
  /** Takes the manager to the Location group's time zone field. */
  openLocationSettings: () => void;
  /** The rate in effect changed on the server; keeps the settings screen's own copy current. */
  onRateInEffect: (units: number) => void;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  newKey: () => string;
  /** Actual confirmation from the ordinary settings read, absent while unknown. */
  confirmation?: () => { at: string; byName: string | null } | null;
  /** LOCSET1 (B11): non-null only while a session that may confirm sees an unconfirmed rate. The Confirm command is the
   * screen's own (admin.ts); this section only draws its button and status inside the one Sales tax card. */
  confirmAction?: () => { busy: boolean; onConfirm: () => void } | null;
}

interface Banner {
  kind: "ok" | "danger";
  lead: string;
  text: string;
  offerReload: boolean;
}

const SVG = {
  info: '<svg class="taxs-ico" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M10 9v5M10 6.2v.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  warn: '<svg class="taxs-ico" viewBox="0 0 20 20" aria-hidden="true"><path d="M10 3l8 14H2z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M10 8v4M10 14.2v.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  x: '<svg class="taxs-ico" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M7 7l6 6M13 7l-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  ok: '<svg class="taxs-ico" viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M6.5 10.5l2.4 2.4 4.6-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  chev: '<svg class="taxs-chev" viewBox="0 0 10 16" aria-hidden="true"><path d="M2 2l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
} as const;

interface ElOpts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.cls !== undefined) node.className = opts.cls;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.testid !== undefined) node.dataset["testid"] = opts.testid;
  for (const [name, value] of Object.entries(opts.attrs ?? {})) {
    node.setAttribute(name, value);
  }
  for (const child of children) {
    node.append(child);
  }
  return node;
}

function icon(markup: string): HTMLElement {
  const holder = el("span", { cls: "taxs-ico-holder", attrs: { "aria-hidden": "true" } });
  holder.innerHTML = markup;
  return holder;
}

export interface TaxRateSection {
  /** Fetches the history (when the session may) and draws the section. */
  refresh: () => Promise<void>;
  /** Draws the section from what it already holds (the settings read finished). */
  render: () => void;
  /** Update only the confirmation line, preserving an open rate form. */
  renderConfirmation?: () => void;
  /** Forgets everything; the next employee starts clean. */
  reset: () => void;
}

export function mountTaxRateSection(root: HTMLElement, deps: TaxRateSectionDeps): TaxRateSection {
  let history: TaxRatesDto | null = null;
  let loadState: "idle" | "loading" | "failed" | "denied" = "idle";
  let view: "overview" | "form" = "overview";
  let form: RateFormState | null = null;
  let banner: Banner | null = null;
  let serverField: PlainRefusal["field"] = null;
  let saving = false;
  let lastRequest: { signature: string; key: string } | null = null;
  const openRows = new Set<string>();
  let refs: FormRefs | null = null;
  let loadSequence = 0;

  const context = (): RateContext => {
    const zone = deps.zone();
    return {
      today: todayInZone(new Date(), zone.timeZone),
      inEffectUnits: history?.inEffect?.totalBasisPoints ?? null,
      zoneIsUnconfirmedUtc: zone.unconfirmedUtc,
    };
  };

  async function refresh(): Promise<void> {
    if (!deps.canManage()) {
      history = null;
      loadState = "idle";
      render();
      return;
    }
    const sequence = ++loadSequence;
    loadState = "loading";
    render();
    const result = await getTaxRates(deps.apiBaseUrl(), deps.tenantId());
    if (!deps.isCurrent() || sequence !== loadSequence) {
      return;
    }
    if (result.status === 200 && Array.isArray(result.body.sets)) {
      history = result.body;
      loadState = "idle";
      if (history.inEffect !== null) {
        deps.onRateInEffect(history.inEffect.totalBasisPoints);
      }
    } else if (result.status === 403) {
      history = null;
      loadState = "denied";
    } else {
      loadState = "failed";
    }
    render();
  }

  function reset(): void {
    loadSequence += 1;
    history = null;
    loadState = "idle";
    view = "overview";
    form = null;
    banner = null;
    serverField = null;
    saving = false;
    lastRequest = null;
    openRows.clear();
    refs = null;
    root.replaceChildren();
  }

  // -------------------------------------------------------------------
  // Drawing
  // -------------------------------------------------------------------

  function render(): void {
    refs = null;
    root.replaceChildren();
    root.dataset["testid"] = "admin-tax-rate-section";
    const manage = deps.canManage() && loadState !== "denied";
    const zone = deps.zone();

    const head = el("div", { cls: "taxs-head" });
    const titles = el("div", { cls: "taxs-head-text" });
    // HIGFIX4A (design/style-guide.md section 11): what the card is for is hover help on its title, not a line under it.
    const titleEl = el("h2", { cls: "taxs-title", text: "Sales tax" });
    hoverHelp(
      titleEl,
      "Tax is added to the menu price when a guest pays.",
      manage ? "The rate guests see is the total of the parts below." : "",
    );
    titles.append(titleEl);
    head.append(titles);
    const showHeadButton =
      manage && view === "overview" && history !== null && loadState === "idle";
    if (showHeadButton) {
      head.append(
        el("button", {
          // LOCSET1 (B11): one primary per card. While the rate waits to be confirmed, Confirm owns the primary.
          cls: deps.confirmAction?.() != null ? "taxs-btn-secondary" : "taxs-btn-primary",
          text: "Record a new rate",
          testid: "tax-record-button",
          attrs: { type: "button" },
        }),
      );
      head.querySelector("button")?.addEventListener("click", () => openForm(false));
    }
    root.append(head);

    if (!manage) {
      renderViewOnly();
      return;
    }

    if (banner !== null && view === "overview") {
      root.append(bannerNode(banner, false));
    }
    // While the form is open the date carries the same reminder inline, so the banner is not shown twice.
    if (zone.unconfirmedUtc && view !== "form") {
      root.append(utcWarning());
    }

    if (loadState === "loading" && history === null) {
      root.append(el("p", { cls: "taxs-help", text: "Loading Sales tax.", testid: "tax-loading" }));
      return;
    }
    if (loadState === "failed" || history === null) {
      const failed = el("div", { cls: "taxs-note taxs-note--danger", testid: "tax-load-failed" });
      failed.append(icon(SVG.x));
      const text = el("div", { cls: "taxs-note-text" });
      text.append(
        el("b", { text: "Sales tax could not be loaded." }),
        document.createTextNode(" Check the connection and try again."),
      );
      const retry = el("button", {
        cls: "taxs-btn-secondary",
        text: "Try again",
        attrs: { type: "button" },
        testid: "tax-load-retry",
      });
      retry.addEventListener("click", () => void refresh());
      text.append(retry);
      failed.append(text);
      root.append(failed);
      return;
    }

    const ctx = context();
    const model = buildRateView(history, ctx.today, zone.timeZone);
    if (view === "form" && form !== null) {
      root.append(renderForm(ctx));
    }
    root.append(renderInEffect(model));
    if (model.comingUp !== null) root.append(renderComingUp(model));
    root.append(renderHistory(model));
  }

  function renderViewOnly(): void {
    const card = el("div", { cls: "taxs-card", testid: "tax-view-only" });
    const viewOnlyTag = el("span", { cls: "taxs-tag", text: "View only" });
    hoverHelp(
      viewOnlyTag,
      "Changing sales tax needs the Manage sales tax permission.",
      "Ask an owner, or ask them to add it to your role under Team.",
    );
    card.append(viewOnlyTag);
    const big = el("div", { cls: "taxs-big" });
    big.append(
      el("span", {
        cls: "taxs-big-n",
        text: formatPercent(deps.combinedUnits()),
        testid: "tax-view-only-rate",
      }),
      el("span", { cls: "taxs-big-w", text: "total sales tax today" }),
    );
    card.append(big);
    // HIGFIX4A (F-24): one plain sentence, not a blue box with a bold lead; the rest is the tag's hover help above.
    card.append(el("p", { cls: "taxs-view-only-line", text: "You can see the rate but not change it." }));
    root.append(card);
  }

  function utcWarning(): HTMLElement {
    const note = el("div", { cls: "taxs-note taxs-note--warn", testid: "tax-utc-warning" });
    note.append(icon(SVG.warn));
    const text = el("div", { cls: "taxs-note-text" });
    text.append(
      el("b", { text: "This location's business day is still on UTC." }),
      document.createTextNode(
        " A rate starts on a date in the location's business day, not on your device clock, so a start date can land a day early or late until the time zone is set. Set it first.",
      ),
    );
    const open = el("button", {
      cls: "taxs-btn-secondary",
      text: "Open Location settings",
      attrs: { type: "button" },
      testid: "tax-open-location",
    });
    open.addEventListener("click", () => deps.openLocationSettings());
    text.append(open);
    note.append(text);
    return note;
  }

  function bannerNode(value: Banner, inForm: boolean): HTMLElement {
    const note = el("div", {
      cls: `taxs-note taxs-note--${value.kind}`,
      testid: inForm ? "tax-form-banner" : "tax-banner",
      attrs: { role: value.kind === "danger" ? "alert" : "status" },
    });
    note.append(icon(value.kind === "danger" ? SVG.x : SVG.ok));
    const text = el("div", { cls: "taxs-note-text" });
    text.append(el("b", { text: value.lead }), document.createTextNode(` ${value.text}`));
    if (value.offerReload) {
      const reload = el("button", {
        cls: "taxs-btn-secondary",
        text: "Reload Sales tax",
        attrs: { type: "button" },
        testid: "tax-reload",
      });
      reload.addEventListener("click", () => {
        banner = null;
        closeForm();
        void refresh();
      });
      text.append(reload);
    }
    note.append(text);
    return note;
  }

  function partRowsNode(rows: readonly PartRow[], totalText: string): HTMLElement {
    const list = el("div", { cls: "taxs-parts" });
    for (const row of rows) {
      const line = el("div", { cls: `taxs-part${row.zero ? " taxs-part--zero" : ""}` });
      line.append(
        el("span", { cls: "taxs-part-n", text: row.label }),
        el("span", { cls: "taxs-part-v", text: row.text }),
      );
      list.append(line);
    }
    if (!(rows.length === 1 && rows[0]?.label.startsWith("Combined"))) {
      const total = el("div", { cls: "taxs-part taxs-part--total" });
      total.append(
        el("span", { cls: "taxs-part-n", text: "Total rate" }),
        el("span", { cls: "taxs-part-v", text: totalText }),
      );
      list.append(total);
    }
    return list;
  }

  function renderInEffect(model: RateView): HTMLElement {
    const card = el("section", { cls: "taxs-card", testid: "tax-in-effect" });
    const top = el("div", { cls: "taxs-card-head" });
    const left = el("div");
    left.append(
      el("h3", { cls: "taxs-card-title", text: "In effect now" }),
      el("p", { cls: "taxs-help", text: "Every new payment uses this rate today." }),
    );
    top.append(left);
    const pendingConfirm = deps.confirmAction?.() ?? null;
    if (pendingConfirm !== null) {
      // LOCSET1 (B11): one status chip for the one fact, in the card the rate is shown in.
      top.append(
        el("span", {
          cls: "taxs-tag",
          text: "Not confirmed",
          testid: "admin-tax-rate-confirm-status",
        }),
      );
    } else if (model.inEffect !== null) {
      if (model.inEffect.isUnsplit) {
        top.append(el("span", { cls: "taxs-tag", text: "One combined rate" }));
      } else if (model.inEffect.since !== null) {
        top.append(
          el("span", {
            cls: "taxs-tag taxs-tag--ok",
            text: `Since ${formatDay(model.inEffect.since)}`,
          }),
        );
      }
    }
    card.append(top);
    if (model.inEffect === null) {
      card.append(
        el("p", {
          cls: "taxs-help",
          text: "No sales tax rate is recorded yet.",
          testid: "tax-in-effect-none",
        }),
      );
      if (pendingConfirm !== null) card.append(...confirmBlock(pendingConfirm));
      return card;
    }
    const big = el("div", { cls: "taxs-big" });
    big.append(
      el("span", {
        cls: "taxs-big-n",
        text: formatPercent(model.inEffect.totalUnits),
        testid: "tax-in-effect-total",
      }),
      el("span", { cls: "taxs-big-w", text: "total sales tax" }),
    );
    card.append(big, partRowsNode(model.inEffect.parts, formatPercent(model.inEffect.totalUnits)));
    const confirmation = confirmationLine();
    if (confirmation !== null) card.append(confirmation);
    if (pendingConfirm !== null) card.append(...confirmBlock(pendingConfirm));
    if (model.inEffect.isUnsplit) {
      const note = el("div", { cls: "taxs-note taxs-note--info", testid: "tax-unsplit-note" });
      note.append(icon(SVG.info));
      const text = el("div", { cls: "taxs-note-text" });
      text.append(
        el("b", { text: "This rate is not split into parts yet." }),
        document.createTextNode(
          " Splitting it changes nothing on a guest's check and nothing in the past. It lets the tax report show state, county and transit separately. Have your accountant's numbers handy.",
        ),
      );
      note.append(text);
      card.append(note);
      if (view === "overview") {
        const split = el("button", {
          cls: "taxs-btn-secondary",
          text: "Split it into parts",
          attrs: { type: "button" },
          testid: "tax-split-button",
        });
        split.addEventListener("click", () => openForm(true));
        card.append(split);
      }
    }
    return card;
  }

  /** LOCSET1 (B11): the go-live confirmation, drawn inside the Sales tax card. Same command, same ids as the old second card. */
  function confirmBlock(action: { busy: boolean; onConfirm: () => void }): HTMLElement[] {
    const help = el("p", {
      cls: "taxs-help",
      text: "Card payments stay off until you confirm the sales tax rate, or record the right one.",
      testid: "admin-tax-rate-confirm-help",
      attrs: { id: "admin-tax-rate-confirm-help" },
    });
    const confirm = el("button", {
      cls: "taxs-btn-primary",
      text: "Confirm sales tax rate",
      testid: "admin-tax-rate-confirm-button",
      attrs: { type: "button", id: "admin-tax-rate-confirm-button", "aria-describedby": "admin-tax-rate-confirm-help" },
    });
    confirm.disabled = action.busy;
    confirm.addEventListener("click", () => action.onConfirm());
    const area = el("div", { cls: "taxs-confirm", testid: "admin-tax-rate-confirm", attrs: { id: "admin-tax-rate-confirm" } });
    area.append(help, confirm);
    return [area];
  }

  function confirmationLine(): HTMLElement | null {
    const confirmation = deps.confirmation?.();
    if (confirmation == null) return null;
    const date = new Date(confirmation.at);
    if (Number.isNaN(date.getTime())) return null;
    const day = date.toLocaleDateString();
    return el("p", {
      cls: "taxs-fine",
      testid: "tax-in-effect-confirmation",
      text: confirmation.byName === null
        ? `Confirmed ${day}.`
        : `Confirmed by ${confirmation.byName}, ${day}.`,
    });
  }

  function renderConfirmation(): void {
    const card = root.querySelector('[data-testid="tax-in-effect"]');
    // LOCSET1 (B11): the card also carries the Confirm button and its status chip, so it is redrawn whole, but only
    // while the overview shows: an open rate form is never thrown away.
    if (card !== null && view === "overview") {
      render();
      return;
    }
    if (card === null || card.querySelector('[data-testid="tax-in-effect-total"]') === null) return;
    card.querySelector('[data-testid="tax-in-effect-confirmation"]')?.remove();
    const line = confirmationLine();
    if (line !== null) card.append(line);
  }

  function renderComingUp(model: RateView): HTMLElement {
    const card = el("section", {
      cls: `taxs-card${model.comingUp === null ? "" : " taxs-card--sched"}`,
      testid: "tax-coming-up",
    });
    const top = el("div", { cls: "taxs-card-head" });
    const left = el("div");
    left.append(el("h3", { cls: "taxs-card-title", text: "Coming up" }));
    if (model.comingUp === null) {
      left.append(el("p", { cls: "taxs-help", text: "Nothing is scheduled." }));
      top.append(left);
      card.append(top);
      return card;
    }
    const next = model.comingUp;
    left.append(
      el("p", {
        cls: "taxs-help",
        text: `Starts on ${formatDay(next.startsOn)}, ${daysText(next.inDays)}. Until then the rate above stays.`,
      }),
    );
    top.append(left, el("span", { cls: "taxs-tag taxs-tag--info", text: "Scheduled" }));
    card.append(top);
    const big = el("div", { cls: "taxs-big" });
    big.append(
      el("span", { cls: "taxs-big-n", text: next.totalText, testid: "tax-coming-up-total" }),
    );
    const w = el("span", { cls: "taxs-big-w", text: `total from ${formatDay(next.startsOn)} ` });
    if (next.deltaText !== "" && next.deltaText !== "no change") {
      w.append(el("b", { text: `(${next.deltaText} from today)` }));
    }
    big.append(w);
    card.append(big, partRowsNode(next.parts, next.totalText));
    card.append(el("p", { cls: "taxs-fine", text: next.footnote }));
    const scheduledSet = history?.sets.find((set) => set.rateSetId === next.key);
    if (scheduledSet !== undefined && view === "overview") {
      const status = el("p", { cls: "taxs-help", text: "Checking this scheduled rate's confirmation." });
      const confirm = el("button", {
        cls: "taxs-btn-secondary", text: "Confirm this scheduled rate",
        attrs: { type: "button" }, testid: "tax-coming-up-confirm",
      });
      confirm.disabled = true;
      card.append(status, confirm);
      void getLocationMoneyConfirmation(
        deps.apiBaseUrl(), deps.tenantId(), scheduledSet.locationId, "tax_rate", next.key,
      ).then((result) => {
        if (!deps.isCurrent() || !root.contains(card)) return;
        if (result.status !== 200) {
          status.textContent = "Could not check this rate's confirmation. Reload Sales tax to try again.";
          return;
        }
        status.textContent = result.body.confirmed
          ? "Confirmed for live card charges when this rate takes effect."
          : "Confirm this rate before it takes effect to keep live card charges available.";
        confirm.disabled = result.body.confirmed;
      });
      confirm.addEventListener("click", async () => {
        confirm.disabled = true;
        const result = await postLocationMoneyConfirmation(
          deps.apiBaseUrl(), deps.tenantId(), scheduledSet.locationId, "tax_rate", next.key,
        );
        if (!deps.isCurrent() || !root.contains(card)) return;
        if (result.status !== 200 && result.status !== 201) {
          status.textContent = "Could not confirm this rate. Reload Sales tax to try again.";
          confirm.disabled = false;
          return;
        }
        await refresh();
      });
    }
    return card;
  }

  function historyRow(row: SetView): HTMLElement {
    const details = el("details", { cls: "taxs-hist", testid: "tax-history-row" });
    details.dataset["setNo"] = String(row.setNo);
    details.open = openRows.has(row.key);
    details.addEventListener("toggle", () => {
      if (details.open) openRows.add(row.key);
      else openRows.delete(row.key);
    });
    const summary = el("summary", { cls: "taxs-hist-sum" });
    const tx = el("div", { cls: "taxs-hist-tx" });
    tx.append(
      el("p", { cls: "taxs-hist-1", text: row.title }),
      el("div", { cls: "taxs-hist-2", text: row.subtitle }),
    );
    const tag = el("span", {
      cls: `taxs-tag${row.status === "scheduled" ? " taxs-tag--info" : row.status === "in_effect" ? " taxs-tag--ok" : " taxs-tag--muted"}`,
      text: row.statusLabel,
    });
    summary.append(tx, tag, el("span", { cls: "taxs-hist-rate", text: row.totalText }));
    const chev = el("span", { cls: "taxs-ico-holder", attrs: { "aria-hidden": "true" } });
    chev.innerHTML = SVG.chev;
    summary.append(chev);
    details.append(summary);
    const body = el("div", { cls: "taxs-hist-body" });
    body.append(
      partRowsNode(row.parts, row.totalText),
      el("p", { cls: "taxs-who", text: row.footnote }),
    );
    details.append(body);
    return details;
  }

  function renderHistory(model: RateView): HTMLElement {
    const card = el("section", { cls: "taxs-card", testid: "tax-history" });
    card.append(
      el("h3", { cls: "taxs-card-title", text: "History" }),
      el("p", {
        cls: "taxs-help",
        text: "Every rate this location has had, newest first. Tap a row to see its parts.",
      }),
    );
    if (model.history.length === 0) {
      card.append(
        el("p", {
          cls: "taxs-help",
          text: "No rate has been recorded yet. The rate in effect above is where this location started.",
          testid: "tax-history-empty",
        }),
      );
    }
    for (const row of model.history) {
      card.append(historyRow(row));
    }
    return card;
  }

  // -------------------------------------------------------------------
  // The form
  // -------------------------------------------------------------------

  interface FormRefs {
    partInputs: Record<TaxPartKey, HTMLInputElement>;
    partErrors: Record<TaxPartKey | "combined", HTMLElement>;
    partFields: Record<TaxPartKey | "combined", HTMLElement>;
    combinedInput: HTMLInputElement;
    splitBlock: HTMLElement;
    combinedBlock: HTMLElement;
    modeSplit: HTMLButtonElement;
    modeCombined: HTMLButtonElement;
    chips: { iso: string | null; node: HTMLButtonElement }[];
    dateInput: HTMLInputElement;
    dateError: HTMLElement;
    dateErrorText: HTMLElement;
    utcNote: HTMLElement | null;
    pastWarning: HTMLElement;
    result: HTMLElement;
    resultLabel: HTMLElement;
    resultTotal: HTMLElement;
    resultStarts: HTMLElement;
    resultCompare: HTMLElement;
    saveLine: HTMLElement;
    save: HTMLButtonElement;
  }

  function openForm(splitting: boolean): void {
    if (history === null) return;
    const ctx = context();
    form = initialFormState(history.inEffect, ctx.today, splitting);
    if (!splitting && history.inEffect !== null) {
      const unsplit =
        history.inEffect.components.length === 1 &&
        history.inEffect.components[0]?.component === "unsplit";
      if (unsplit) {
        form.mode = "combined";
      }
    }
    banner = null;
    serverField = null;
    lastRequest = null;
    view = "form";
    render();
    refs?.partInputs.state.focus({ preventScroll: false });
  }

  function closeForm(): void {
    view = "overview";
    form = null;
    serverField = null;
    saving = false;
    render();
  }

  function renderForm(ctx: RateContext): HTMLElement {
    const current = form;
    if (current === null) {
      return el("div");
    }
    const card = el("section", { cls: "taxs-card taxs-card--form", testid: "tax-form" });
    const top = el("div", { cls: "taxs-card-head" });
    const left = el("div");
    left.append(
      el("h3", { cls: "taxs-card-title", text: "Record a new rate" }),
      el("p", {
        cls: "taxs-help",
        text: "Enter the parts and the day the new rate starts. Nothing changes until you record it.",
      }),
    );
    const cancel = el("button", {
      cls: "taxs-btn-secondary",
      text: "Cancel",
      attrs: { type: "button" },
      testid: "tax-cancel",
    });
    cancel.addEventListener("click", () => closeForm());
    top.append(left, cancel);
    card.append(top);

    const bannerSlot = el("div", { attrs: { "data-slot": "banner" } });
    if (banner !== null) {
      bannerSlot.append(bannerNode(banner, true));
    }
    card.append(bannerSlot);

    // Mode switch
    const mode = el("div", {
      cls: "taxs-seg",
      attrs: { role: "group", "aria-label": "How to enter the rate" },
    });
    const modeSplit = el("button", {
      cls: "taxs-seg-btn",
      text: "Split into parts",
      attrs: { type: "button" },
      testid: "tax-mode-split",
    });
    const modeCombined = el("button", {
      cls: "taxs-seg-btn",
      text: "One combined rate",
      attrs: { type: "button" },
      testid: "tax-mode-combined",
    });
    mode.append(modeSplit, modeCombined);
    card.append(mode);

    const partInputs = {} as Record<TaxPartKey, HTMLInputElement>;
    const partErrors = {} as Record<TaxPartKey | "combined", HTMLElement>;
    const partFields = {} as Record<TaxPartKey | "combined", HTMLElement>;

    const percentField = (
      key: TaxPartKey | "combined",
      initial: string,
      label: string,
      help: string,
      onChange: (value: string) => void,
    ): { row: HTMLElement; input: HTMLInputElement } => {
      const row = el("div", { cls: "taxs-frow" });
      const text = el("div", { cls: "taxs-fl" });
      const id = `tax-field-${key}`;
      text.append(el("label", { text: label, attrs: { for: id } }));
      // SET21: a field with nothing to say beyond its label carries no help line.
      if (help !== "") {
        text.append(el("div", { cls: "taxs-fh", text: help, attrs: { id: `${id}-help` } }));
      }
      const error = el("div", {
        cls: "taxs-ferr",
        testid: `tax-${key}-error`,
        attrs: { id: `${id}-error`, role: "alert" },
      });
      error.hidden = true;
      text.append(error);
      const step = el("div", { cls: "taxs-step" });
      const minus = el("button", {
        cls: "taxs-sb",
        text: "−",
        attrs: { type: "button", "aria-label": `Lower ${label} by 0.25` },
        testid: `tax-${key}-minus`,
      });
      const plus = el("button", {
        cls: "taxs-sb",
        text: "+",
        attrs: { type: "button", "aria-label": `Raise ${label} by 0.25` },
        testid: `tax-${key}-plus`,
      });
      const pct = el("span", { cls: "taxs-pct" });
      const input = el("input", {
        cls: "taxs-input",
        testid: key === "combined" ? "tax-combined-input" : `tax-part-${key}`,
        attrs: {
          id,
          type: "text",
          inputmode: "decimal",
          autocomplete: "off",
          "aria-describedby": help === "" ? `${id}-error` : `${id}-help ${id}-error`,
          value: initial,
        },
      });
      input.value = initial;
      pct.append(input);
      input.addEventListener("input", () => onChange(input.value));
      input.addEventListener("blur", () => {
        input.value = input.value.trim();
      });
      minus.addEventListener("click", () => {
        input.value = stepPercentText(input.value, -1);
        onChange(input.value);
      });
      plus.addEventListener("click", () => {
        input.value = stepPercentText(input.value, 1);
        onChange(input.value);
      });
      step.append(minus, pct, plus);
      row.append(text, step);
      partErrors[key] = error;
      partFields[key] = pct;
      return { row, input };
    };

    const splitBlock = el("div", { testid: "tax-split-block" });
    splitBlock.append(el("div", { cls: "taxs-group-t", text: "The parts" }));
    const partsWrap = el("div", { cls: "taxs-parts-form" });
    for (const key of TAX_PART_ORDER) {
      const built = percentField(
        key,
        current.parts[key],
        TAX_PART_LABELS[key],
        TAX_PART_HELP[key],
        (value) => {
          current.parts[key] = value;
          touch();
        },
      );
      partInputs[key] = built.input;
      partsWrap.append(built.row);
    }
    splitBlock.append(partsWrap);
    card.append(splitBlock);

    const combinedBlock = el("div", { testid: "tax-combined-block" });
    combinedBlock.append(el("div", { cls: "taxs-group-t", text: "The rate" }));
    const combinedBuilt = percentField(
      "combined",
      current.combined,
      "Combined rate",
      `${deps.rangeText()}. The tax report cannot show the parts.`,
      (value) => {
        current.combined = value;
        touch();
      },
    );
    combinedBlock.append(combinedBuilt.row);
    card.append(combinedBlock);

    // The day it starts
    card.append(el("div", { cls: "taxs-group-t", text: "The day it starts" }));
    card.append(
      el("div", {
        cls: "taxs-fh",
        text: "Sales on and after this day use the rate.",
        attrs: { id: "tax-field-date-help" },
      }),
    );
    const grid = el("div", {
      cls: "taxs-dategrid",
      attrs: { role: "group", "aria-label": "Start day" },
    });
    const chips: { iso: string | null; node: HTMLButtonElement }[] = [];
    for (const preset of startPresets(ctx.today)) {
      const node = el("button", {
        cls: "taxs-chip",
        text: preset.label,
        attrs: { type: "button" },
        testid: "tax-date-chip",
      });
      node.dataset["iso"] = preset.iso;
      node.addEventListener("click", () => {
        current.startDate = preset.iso;
        touch();
      });
      chips.push({ iso: preset.iso, node });
      grid.append(node);
    }
    const pick = el("button", {
      cls: "taxs-chip",
      text: "Pick a date",
      attrs: { type: "button" },
      testid: "tax-date-pick",
    });
    pick.addEventListener("click", () => {
      dateInput.focus();
      try {
        dateInput.showPicker();
      } catch (error) {
        // showPicker needs a user gesture and a browser that has it; the field itself still works.
        if (!(error instanceof DOMException)) throw error;
      }
    });
    chips.push({ iso: null, node: pick });
    grid.append(pick);
    card.append(grid);

    const dateWrap = el("div", { cls: "taxs-dateinput" });
    const dateInput = el("input", {
      cls: "taxs-input taxs-input--date",
      testid: "tax-date-input",
      attrs: {
        type: "date",
        id: "tax-field-date",
        min: MIN_START_DATE,
        max: MAX_START_DATE,
        "aria-label": "Start day",
        "aria-describedby": "tax-field-date-help tax-field-date-error",
      },
    });
    dateInput.value = current.startDate;
    dateInput.addEventListener("input", () => {
      current.startDate = dateInput.value;
      touch();
    });
    dateWrap.append(dateInput);
    card.append(dateWrap);
    const dateError = el("div", {
      cls: "taxs-ferr",
      testid: "tax-date-error",
      attrs: { id: "tax-field-date-error", role: "alert" },
    });
    const dateErrorText = el("span");
    dateError.append(icon(SVG.x), dateErrorText);
    dateError.hidden = true;
    card.append(dateError);

    let utcNote: HTMLElement | null = null;
    if (ctx.zoneIsUnconfirmedUtc) {
      utcNote = el("div", {
        cls: "taxs-note taxs-note--warn taxs-note--tight",
        testid: "tax-utc-note",
      });
      utcNote.append(icon(SVG.warn));
      const text = el("div", { cls: "taxs-note-text" });
      text.append(
        el("b", { text: "The time zone is still UTC." }),
        document.createTextNode(
          " This date counts in UTC until you set the time zone in Location.",
        ),
      );
      utcNote.append(text);
      card.append(utcNote);
    }

    const pastWarning = el("div", {
      cls: "taxs-note taxs-note--warn",
      testid: "tax-past-warning",
      attrs: { role: "status" },
    });
    pastWarning.append(icon(SVG.warn));
    const pastText = el("div", { cls: "taxs-note-text" });
    pastWarning.append(pastText);
    pastWarning.hidden = true;
    card.append(pastWarning);

    // Result
    const result = el("div", { cls: "taxs-result", testid: "tax-result" });
    const left2 = el("div");
    const resultTotal = el("div", { cls: "taxs-result-v", testid: "tax-result-total" });
    const resultLabel = el("div", { cls: "taxs-result-k", testid: "tax-result-label" });
    left2.append(resultLabel, resultTotal);
    const right2 = el("div", { cls: "taxs-result-right" });
    const resultStarts = el("div", {
      cls: "taxs-result-v taxs-result-v--small",
      testid: "tax-result-starts",
    });
    right2.append(el("div", { cls: "taxs-result-k", text: "Starts" }), resultStarts);
    const resultCompare = el("div", { cls: "taxs-result-cmp", testid: "tax-result-compare" });
    result.append(left2, right2, resultCompare);
    card.append(result);

    // Save, at the end of the form, never fixed
    const bar = el("div", { cls: "taxs-savebar" });
    const saveLine = el("div", {
      cls: "taxs-why",
      testid: "tax-save-line",
      attrs: { "aria-live": "polite" },
    });
    const save = el("button", {
      cls: "taxs-btn-primary",
      attrs: { type: "button" },
      testid: "tax-save",
    });
    save.addEventListener("click", () => void submit());
    bar.append(saveLine, save);
    card.append(bar);

    modeSplit.addEventListener("click", () => setMode("split"));
    modeCombined.addEventListener("click", () => setMode("combined"));

    refs = {
      partInputs,
      partErrors,
      partFields,
      combinedInput: combinedBuilt.input,
      splitBlock,
      combinedBlock,
      modeSplit,
      modeCombined,
      chips,
      dateInput,
      dateError,
      dateErrorText,
      utcNote,
      pastWarning,
      result,
      resultLabel,
      resultTotal,
      resultStarts,
      resultCompare,
      saveLine,
      save,
    };
    updateForm();
    return card;
  }

  function setMode(mode: FormMode): void {
    if (form === null) return;
    form.mode = mode;
    touch();
  }

  function touch(): void {
    if (form === null) return;
    form.touched = true;
    serverField = null;
    updateForm();
  }

  /** Redraws everything derived from the typed values, without touching the inputs themselves. */
  function updateForm(): void {
    if (form === null || refs === null || history === null) return;
    const ctx = context();
    const check = checkForm(form, ctx);
    const r = refs;
    const splitMode = form.mode === "split";
    r.splitBlock.hidden = !splitMode;
    r.combinedBlock.hidden = splitMode;
    r.modeSplit.classList.toggle("taxs-seg-btn--on", splitMode);
    r.modeCombined.classList.toggle("taxs-seg-btn--on", !splitMode);
    r.modeSplit.setAttribute("aria-pressed", String(splitMode));
    r.modeCombined.setAttribute("aria-pressed", String(!splitMode));
    const keys: (TaxPartKey | "combined")[] = [...TAX_PART_ORDER, "combined"];
    for (const key of keys) {
      const message = check.partErrors[key];
      const node = r.partErrors[key];
      node.hidden = message === undefined;
      node.replaceChildren();
      if (message !== undefined) {
        node.append(icon(SVG.x), el("span", { text: message }));
      }
      const input = key === "combined" ? r.combinedInput : r.partInputs[key];
      const bad = message !== undefined;
      input.setAttribute("aria-invalid", String(bad));
      r.partFields[key].classList.toggle("taxs-pct--bad", bad);
    }
    for (const chip of r.chips) {
      const on =
        chip.iso === null
          ? !r.chips.some((c) => c.iso !== null && c.iso === form?.startDate) &&
            form.startDate !== ""
          : chip.iso === form.startDate;
      chip.node.classList.toggle("taxs-chip--on", on);
      chip.node.setAttribute("aria-pressed", String(on));
    }
    if (r.dateInput.value !== form.startDate) {
      r.dateInput.value = form.startDate;
    }
    const dateMessage =
      check.dateError ??
      (serverField === "date"
        ? "That start date is not allowed. Pick a day between 2000 and 2099."
        : null);
    r.dateError.hidden = dateMessage === null;
    r.dateErrorText.textContent = dateMessage ?? "";
    r.dateInput.classList.toggle("taxs-input--bad", dateMessage !== null);
    r.dateInput.setAttribute("aria-invalid", String(dateMessage !== null));

    const warning = pastDateWarning(
      check.startDate,
      ctx.today,
      history.inEffect?.effectiveFrom ?? null,
    );
    r.pastWarning.hidden = warning === null;
    const pastText = r.pastWarning.querySelector(".taxs-note-text");
    if (pastText !== null) {
      pastText.replaceChildren();
      if (warning !== null) {
        pastText.append(
          el("b", { text: warning.lead }),
          document.createTextNode(` ${warning.text}`),
        );
      }
    }

    r.result.classList.toggle("taxs-result--bad", check.bad);
    r.resultLabel.textContent = totalLabel(form.mode);
    r.resultTotal.textContent =
      check.totalUnits === null ? "Not a number yet" : formatPercent(check.totalUnits);
    r.resultStarts.textContent =
      check.startDate === null
        ? "No date yet"
        : check.startDate === ctx.today
          ? `Today, ${formatDay(check.startDate, ctx.today)}`
          : formatDay(check.startDate);
    r.resultCompare.textContent = check.comparison;
    r.saveLine.textContent = check.saveLine;
    r.save.textContent = saving ? "Recording" : check.saveLabel;
    r.save.disabled = !check.canSave || saving;
  }

  async function submit(): Promise<void> {
    if (form === null || saving) return;
    const ctx = context();
    const check = checkForm(form, ctx);
    if (!check.canSave) {
      updateForm();
      return;
    }
    const draft = buildPutBody(form, ctx, "pending");
    const signature = requestSignature(draft);
    if (lastRequest === null || lastRequest.signature !== signature) {
      lastRequest = { signature, key: deps.newKey() };
    }
    const body = { ...draft, idempotencyKey: lastRequest.key };
    saving = true;
    updateForm();
    const result = await putTaxRates(deps.apiBaseUrl(), deps.tenantId(), body);
    if (!deps.isCurrent()) {
      return;
    }
    saving = false;
    if (result.status === 200 || result.status === 201) {
      const total = check.totalUnits ?? 0;
      const start = check.startDate ?? ctx.today;
      banner = {
        kind: "ok",
        lead: "Recorded.",
        text: recordedMessage(total, start, ctx.today),
        offerReload: false,
      };
      view = "overview";
      form = null;
      lastRequest = null;
      await refresh();
      return;
    }
    const refusal = result.networkError
      ? mapRefusal(0, undefined)
      : mapRefusal(result.status, result.body.message);
    banner = {
      kind: "danger",
      lead: refusal.lead,
      text: refusal.text,
      offerReload: refusal.offerReload,
    };
    serverField = refusal.field;
    // Keep every typed value: redraw only the banner and the derived parts, never the inputs.
    const slot = root.querySelector('[data-slot="banner"]');
    if (slot !== null) {
      slot.replaceChildren(bannerNode(banner, true));
      slot.querySelector<HTMLElement>('[role="alert"]')?.scrollIntoView({ block: "nearest" });
    }
    updateForm();
  }

  return { refresh, render, reset, renderConfirmation };
}
